import { Image } from "@/components/common/Image"
import { FormCountrySelect } from "@/components/Form/CoutrySelect"
import { Input } from "@/components/Form/Input"
import { Autocomplete } from "@/components/Selector/Autocomplete"
import { ALL_DOCUMENT_EXTENSIONS } from "@/constants/CardConstants"
import { File } from "@/Images"
import { EURO_OPTION, IBAN, IBANContext, ITransferForm, USD_OPTION, useDropUpload, useIBANStates } from "@usher/pe-client-front-end-lib"
import { useContext, useEffect } from "react"
import { Controller, useFormContext } from "react-hook-form"
import { FormattedMessage, useIntl } from "react-intl"
import { IBANSelect } from "../../../IBANSelect"
import { PaymentSystemLogo } from "../../../PaymentSystemLogo"
import { FormRow } from "./FormRow"

const ACCOUNT_TYPE_OPTIONS = [
  { label: "Individual", value: "1" },
  { label: "Company", value: "2" },
]
const CURRENCY_OPTIONS = [EURO_OPTION, USD_OPTION]
interface Props {
  iban?: IBAN
  onIbanSelect: (iban: IBAN) => void
}
export const TransferForm = ({ iban, onIbanSelect }: Props) => {
  const { $t: t } = useIntl()
  const { currency, paymentSystem } = useContext(IBANContext)
  const currencyOption = CURRENCY_OPTIONS.find((option) => option.value === currency)
  const { control, setValue, watch } = useFormContext<ITransferForm>()
  const { bankLoading, receiverLoading, states } = useIBANStates(watch)
  const hasReceiverStates = (states.receiverStates || []).length > 0
  const hasBankStates = (states.bankStates || []).length > 0
  const { files, getRootProps, getInputProps, onDelete, setFiles } = useDropUpload({
    accept: {
      "image/*": [],
      "application/*": [...ALL_DOCUMENT_EXTENSIONS.map((ext) => `.${ext}`)],
    },
    multiple: true,
  })
  const docUploads = watch("doc_upload") ?? []
  useEffect(() => {
    setValue("doc_upload", files)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files])
  useEffect(() => {
    setFiles?.(docUploads)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <div className="transfer-form border !border-b-0">
      <FormRow
        title={
          <div className="flex items-start">
            <PaymentSystemLogo className="inline-block md:hidden" />
            <h1>Transfer Details (Outgoing Transfer)</h1>
            <PaymentSystemLogo className="hidden md:inline-block ml-2" />
          </div>
        }
      >
        <div>
          <div className="mb-2.5">
            <IBANSelect
              disabled
              iban={iban}
              onChange={(option) => onIbanSelect((option?.meta as IBAN) || null)}
              autoSelectFirst
              paymentSystem={paymentSystem}
            />
          </div>
          <div className="flex gap-2">
            <Input.Form placeholder="Amount" {...{ control, name: "amount" }} className="flex-1" required inputMode="numeric" isNumberField />
            <Autocomplete disabled options={[currencyOption!]} value={currencyOption} />
          </div>
        </div>
      </FormRow>
      <FormRow title="Receiver's Info">
        <div className="flex flex-col gap-2.5">
          <Controller
            {...{ control, name: "account_type", rules: { required: { value: true, message: t({ id: "required" }) } } }}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                error={error?.message}
                options={ACCOUNT_TYPE_OPTIONS}
                // @ts-ignore
                value={ACCOUNT_TYPE_OPTIONS.find((option) => option.value === field.value) ?? null}
                onChange={(value) => field.onChange(value?.value)}
                label="Select Recepient"
              />
            )}
          />
          <Input.Form placeholder="Beneficiary Name" {...{ control, name: "beneficiary_name" }} required mode="wa" />
          <div className="grid md:grid-cols-2 gap-2.5">
            <FormCountrySelect {...{ control, name: "receiver_country" }} label="Country" required />
            <Input.Form placeholder="City" {...{ control, name: "receiver_city" }} required mode="wan" />
            <Controller
              {...{ control, name: "receiver_state", rules: { required: { value: hasReceiverStates, message: t({ id: "required" }) } } }}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  loading={receiverLoading}
                  options={states.receiverStates.map((state) => ({
                    label: state.name,
                    value: state.code,
                  }))}
                  onChange={field.onChange}
                  value={field.value}
                  label="State"
                  searchable
                  error={error?.message}
                />
              )}
            />

            <Input.Form placeholder="ZIP" {...{ control, name: "receiver_zip" }} required mode="an" uppercase />
            <Input.Form
              placeholder="Address Line"
              {...{ control, name: "receiver_address", maxLength: 35 }}
              required
              mode="wan"
              helper="Do not include country, city, state and ZIP in this field."
            />
            <Input.Form
              placeholder="Address Line 2"
              {...{ control, name: "receiver_address2", maxLength: 35 }}
              required
              mode="wan"
              helper="Do not include country, city, state and ZIP in this field."
            />
          </div>

          <Input.Form placeholder="IBAN/Account Number" {...{ control, name: "iban_account_number" }} required mode="an" uppercase />
        </div>
      </FormRow>
      <FormRow title="Receiver's Bank Info">
        <div className="flex flex-col gap-2.5">
          <Input.Form placeholder="Bank Name" {...{ control, name: "bank_name" }} required mode="an" />
          <Input.Form placeholder="SWIFT/BIC" {...{ control, name: "swift" }} required mode="an" uppercase />
          <div className="grid md:grid-cols-2 gap-2.5">
            <FormCountrySelect {...{ control, name: "bank_country" }} label="Country" required />
            <Input.Form placeholder="City" {...{ control, name: "receiver_bank_city" }} required mode="wan" />
            <Controller
              {...{ control, name: "receiver_bank_state", rules: { required: { value: hasBankStates, message: t({ id: "required" }) } } }}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  loading={bankLoading}
                  options={states.bankStates.map((state) => ({
                    label: state.name,
                    value: state.code,
                  }))}
                  onChange={field.onChange}
                  value={field.value}
                  label="State"
                  searchable
                  error={error?.message}
                />
              )}
            />
            <Input.Form placeholder="ZIP" {...{ control, name: "receiver_bank_zip" }} required mode="an" uppercase />
            <Input.Form
              placeholder="Address Line"
              {...{ control, name: "address", maxLength: 35 }}
              required
              mode="wan"
              helper="Do not include country, city, state and ZIP in this field."
            />
            <Input.Form
              placeholder="Address Line 2"
              {...{ control, name: "address2", maxLength: 35 }}
              required
              mode="wan"
              helper="Do not include country, city, state and ZIP in this field."
            />
          </div>
        </div>
      </FormRow>
      <FormRow title="Transfer Description">
        <div className="">
          <p>Please, provide the instructions for the recepient or the credentials of your transaction</p>
          <p style={{ display: "block" }}>Field limitation: 34 characters per line & 4 lines max</p>
          <Input.Form
            placeholder="Transfer Reason"
            {...{ control, name: "reason" }}
            className="mt-2.5"
            rows={4}
            onInput={(e) => {
              const textarea = e.target as HTMLTextAreaElement
              let lines = textarea.value.split("\n")
              lines = lines.map((line) => line.slice(0, 34))
              if (lines.length > 4) {
                lines = lines.slice(0, 4)
              }
              textarea.value = lines.join("\n")
            }}
          />
        </div>
      </FormRow>
      <FormRow title="Supporting Documents for Your Transfer">
        <p className="application-form__upload-des">
          <FormattedMessage id="pa_203" />
        </p>
        {/* @ts-ignore */}
        <div
          {...getRootProps({
            className: "dropzone application-form__upload-box d-flex",
          })}
        >
          {files.map((file, key) => {
            const extension = file.name.split(".").pop()?.toLowerCase()
            return (
              <div key={key} className="application-form__upload-uploaded">
                {ALL_DOCUMENT_EXTENSIONS.some((ext) => extension?.includes(ext)) ? (
                  <div className="custom-file-icon">
                    <File />
                    {extension?.toUpperCase()}
                  </div>
                ) : (
                  <img src={URL.createObjectURL(file) ?? ""} className="img-fluid" alt="" />
                )}
                <button
                  className="button-reset application-form__upload-delete"
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    onDelete(file)
                  }}
                >
                  <Image src="delete_i.svg" />
                </button>
              </div>
            )
          })}
          <input onClick={(e) => e.stopPropagation()} {...getInputProps()} />
          <div className="application-form__upload-field">
            <label className="application-form__upload-label">
              <Image src="add.svg" className="application-form__upload-add" />
              <span className="application-form__upload-helps d-block px-2">
                <FormattedMessage id="pa_204_1" />
                <span>
                  <FormattedMessage id="pa_204_2" />
                </span>
              </span>
            </label>
          </div>
        </div>
      </FormRow>
    </div>
  )
}
